import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  TextField, 
  Box, 
  FormHelperText, 
  Collapse, 
  List, 
  ListItem, 
  ListItemText,
  Typography,
  Paper,
  Button,
  Chip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';

interface MultilineUrlInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange: (hasInvalidUrls: boolean) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
  validator?: (url: string) => boolean;
}

interface InvalidUrlInfo {
  url: string;
  lineIndex: number;
  startPos: number;
  endPos: number;
}

const Container = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  position: 'relative',
  width: '100%',
}));

const ErrorMessage = styled(FormHelperText)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  marginLeft: '14px',
  marginTop: '8px',
}));

const ErrorDetail = styled(Paper)(({ theme }) => ({
  marginTop: '8px',
  marginBottom: '8px',
  maxHeight: '120px',
  overflowY: 'auto',
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '& .MuiList-root': {
    padding: 0,
  },
  '& .MuiListItem-root': {
    paddingTop: '2px',
    paddingBottom: '2px',
    minHeight: '25px',
    borderBottom: `1px solid ${theme.palette.error.dark}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const UrlErrorItemText = styled(ListItemText)(({ theme }) => ({
  margin: 0,
  width: '100%',
  '& .MuiListItemText-primary': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  '& .url-text': {
    flex: '1 1 auto',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    marginRight: '12px',
    color: 'white',
    fontSize: "12px"
  },
  '& .line-number': {
    color: 'white',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
    fontWeight: 500,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: '1px 6px',
    borderRadius: '8px',
  }
}));


const MultilineUrlInput: React.FC<MultilineUrlInputProps> = ({
  value,
  onChange,
  onValidationChange,
  label = '',
  placeholder = '',
  rows = 4,
  fullWidth = true,
  validator = (url: string) => true,
}) => {
  const [hasError, setHasError] = useState(false);
  const [invalidUrlsInfo, setInvalidUrlsInfo] = useState<InvalidUrlInfo[]>([]);
  const [expanded, setExpanded] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);
  
  const validateUrls = useCallback(() => {
    const lines = value.split('\n');
    const invalidUrlsList: InvalidUrlInfo[] = [];
    
    let currentPosition = 0;
    
    
    lines.forEach((line, lineIndex) => {
      const trimmedUrl = line.trim();
      if (trimmedUrl && !validator(trimmedUrl)) {
        const lineStartPos = currentPosition;
        const urlStartPos = line.indexOf(trimmedUrl) + lineStartPos;
        const urlEndPos = urlStartPos + trimmedUrl.length;
        
        invalidUrlsList.push({
          url: trimmedUrl,
          lineIndex,
          startPos: urlStartPos,
          endPos: urlEndPos
        });
      }
      currentPosition += line.length + 1;
    });
    
    const hasInvalidUrls = invalidUrlsList.length > 0;
    setHasError(hasInvalidUrls);
    setInvalidUrlsInfo(invalidUrlsList);
    onValidationChange(hasInvalidUrls);
    
    if (!hasInvalidUrls) {
      setExpanded(false);
    }
  }, [value, validator, onValidationChange]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      validateUrls();
    }, 150);
    
    return () => clearTimeout(timer);
  }, [value, validateUrls]);
  
  const getSummaryErrorMessage = () => {
    if (invalidUrlsInfo.length === 1) {
      const displayUrl = invalidUrlsInfo[0].url.length > 30 
        ? `${invalidUrlsInfo[0].url.substring(0, 30)}...` 
        : invalidUrlsInfo[0].url;
      return `"${displayUrl}" is not a valid LinkedIn profile URL`;
    }
    
    return `${invalidUrlsInfo.length} URLs are not valid LinkedIn profile URLs`;
  };
  
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
    const navigateToUrl = (urlInfo: InvalidUrlInfo) => {
    if (!textFieldRef.current) return;
    
    const textarea = textFieldRef.current.querySelector('textarea');
    if (!textarea) return;
    
    textarea.focus();
    
    textarea.setSelectionRange(urlInfo.startPos, urlInfo.endPos);
    
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight || '24', 10);
    const scrollPosition = lineHeight * urlInfo.lineIndex;
    textarea.scrollTop = scrollPosition;
  };
  
  return (
    <Container>
      <TextField
        fullWidth={fullWidth}
        multiline
        rows={rows}
        label={label}
        placeholder={placeholder}
        variant="outlined"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={hasError}
        sx={{
          '& .MuiInputBase-root': {
            maxHeight: '150px',
          },
          '& textarea': {
            maxHeight: '150px !important',
            overflowY: 'auto !important',
          },
        }}
        ref={textFieldRef}
      />
      
      {hasError && (
        <>
          <ErrorMessage error onClick={toggleExpanded}>
            <Typography variant="body2" color="error" sx={{ mr: 1 }}>
              {getSummaryErrorMessage()}
            </Typography>
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </ErrorMessage>
          
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <ErrorDetail elevation={0}>
              <List dense disablePadding>
                {invalidUrlsInfo.map((urlInfo, index) => (
                  <ListItem 
                    key={index} 
                    onClick={() => navigateToUrl(urlInfo)}
                    title="Click to navigate to this URL"
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      padding: '4px 16px'
                    }}
                  >
                    <UrlErrorItemText
                      disableTypography
                      primary={
                        <React.Fragment>
                          <span className="url-text">{urlInfo.url}</span>
                          <span className="line-number">Line: {urlInfo.lineIndex + 1}</span>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </ErrorDetail>
          </Collapse>
        </>
      )}
    </Container>
  );
};

export default MultilineUrlInput; 